import { JSX, useContext, useEffect, useMemo, useState } from "react";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { AdvancedExclusionsContext } from "@app/projects/projection-provider/advanced-exclusions/AdvancedExclusionsContext";
import { ExclusionVerifiedSources } from "@app/projects/projection-provider/advanced-exclusions/interfaces";
import PageSection from "@app/shared/PageSection";
import { SearchBox } from "@app/shared/SearchBox";

interface BulkExclusionsVerifiedSourcesSelectProps {
  pageIsValid: (flag: boolean) => void;
}

const BulkExclusionsVerifiedSourcesSelectColumnConfig: ColumnConfig = {
  source: {
    displayText: "Source",
    testId: "source",
  },
};

interface BulkExclusionsVerifiedSourcesSelectData {
  [wafflesKeyColumnName]: string;
  source: string;
}

const BulkExclusionsVerifiedSourcesSelect = (props: BulkExclusionsVerifiedSourcesSelectProps): JSX.Element => {
  const wizardContext = useContext(AdvancedExclusionsContext);
  const [sourceSearchString, setSourceSearchString] = useState("");

  const sources = ExclusionVerifiedSources.verifiedSources.map((source) => { return { key: source, source: source } });

  const renderedSources = useMemo(() => {
    if (!sourceSearchString) {
      return sources.sort((a, b) => a.source > b.source ? 1 : -1);
    }
    const regex = new RegExp(sourceSearchString, "i");
    const filteredSourcesList = sources.filter((sourceItem) => !!sourceItem.source.match(regex));
    return filteredSourcesList.sort((a, b) => a.source > b.source ? 1 : -1);
  }, [sourceSearchString, sources]);

  const handleCheckboxChange = (selectedKeys: string[], isBulk?: boolean) => {
    if (isBulk) {
      let newSelectedSources = [...wizardContext.selectedVerifiedSources];
      if (selectedKeys.length > 0) {
        newSelectedSources.push(...selectedKeys);
      } else {
        const removedIds = renderedSources.map((sourceItem) => sourceItem.source.toString());
        newSelectedSources = newSelectedSources.filter((source) => { return !removedIds.includes(source); });
      }
      wizardContext.setSelectedVerifiedSources([...new Set(newSelectedSources)]);
    } else {
      wizardContext.setSelectedVerifiedSources(selectedKeys);
    }
  };

  useEffect(() => {
    props.pageIsValid(wizardContext.selectedVerifiedSources.length > 0);
  }, [wizardContext.selectedVerifiedSources]);

  return (
    <PageSection>
      <div className="container mt-3">
        <SearchBox
          escapeSearchValue={true}
          fieldsetClass="d-flex"
          inputLabel="Search Sources"
          onSubmit={(value) => setSourceSearchString(value)}
          testId="advanced-exclusions-sources-search"
        />
      </div>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <Waffles<BulkExclusionsVerifiedSourcesSelectData>
              columns={BulkExclusionsVerifiedSourcesSelectColumnConfig}
              data={renderedSources}
              fullWidth={true}
              handleCheckboxChange={handleCheckboxChange}
              selectedKeys={wizardContext.selectedVerifiedSources}
              showCheckboxes={true}
              testId="advanced-exclusions-sources-table"
              totalNumberOfRecords={sources.length}
            />
          </div>
        </div>
      </div>
    </PageSection>
  );
};

export default BulkExclusionsVerifiedSourcesSelect;